/**
 * Lanes under the elevation profile: one per camera shown as a lane (long
 * range cameras), ordered by camera number. A drone's or FPV's lane shows
 * each position as a bar over the route its view areas cover; a moving
 * camera's lane shows the stretches each position rides with the race.
 */
import { getSymbolType } from "./symbols.js";
const byNumber = new Intl.Collator(undefined, { numeric: true });
/** Lanes of the given (visible) items on one track. */
export function profileLanes(items, track) {
    const byCamera = new Map();
    for (const item of items) {
        if (item.profileDisplay !== "lane" || !item.camera)
            continue;
        const group = byCamera.get(item.camera.id) ?? [];
        group.push(item);
        byCamera.set(item.camera.id, group);
    }
    const lanes = [];
    for (const group of byCamera.values()) {
        const camera = group[0].camera;
        const symbol = getSymbolType(camera.icon);
        const ridesRoute = !!symbol.ridesRoute;
        const bars = [];
        const stands = [];
        if (ridesRoute) {
            // Each position rides its stretches; none = the whole route
            for (const item of group) {
                const ranges = item.item.rideRanges ?? [];
                const letter = letterOf(item);
                if (!ranges.length) {
                    bars.push({
                        fromM: 0,
                        toM: track.length,
                        itemId: item.item.id,
                        letter,
                        rangeIndex: null,
                    });
                    continue;
                }
                for (const [rangeIndex, range] of ranges.entries()) {
                    if (range.trackId !== track.id)
                        continue;
                    const fromM = clamp(Math.min(range.fromM, range.toM), track.length);
                    const toM = clamp(Math.max(range.fromM, range.toM), track.length);
                    if (toM > fromM) {
                        bars.push({ fromM, toM, itemId: item.item.id, letter, rangeIndex });
                    }
                }
            }
        }
        else {
            for (const item of group) {
                const letter = letterOf(item);
                for (const c of item.coverage) {
                    if (c.trackId === track.id) {
                        bars.push({
                            fromM: c.fromM,
                            toM: c.toM,
                            itemId: item.item.id,
                            letter,
                            rangeIndex: null,
                        });
                    }
                }
                for (const mark of item.marks) {
                    if (mark.trackId === track.id) {
                        stands.push({ m: mark.m, itemId: item.item.id });
                    }
                }
            }
        }
        if (!bars.length && !stands.length)
            continue;
        lanes.push({
            camera,
            label: group[0].baseLabel,
            color: symbol.color,
            ridesRoute,
            bars: bars.sort((a, b) => a.fromM - b.fromM),
            stands,
        });
    }
    return lanes.sort((a, b) => byNumber.compare(a.camera.number, b.camera.number) ||
        (a.camera.sortOrder ?? 0) - (b.camera.sortOrder ?? 0));
}
function letterOf(item) {
    return item.positionCount > 1 ? (item.item.positionLetter ?? null) : null;
}
function clamp(m, length) {
    return Math.max(0, Math.min(length, m));
}
